import { documentationClient } from 'shared/domain/documentation/documentationClient';
import { ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import { Typography } from '@mui/material';
import { isFileExportAllowed } from 'services/fileService';
import { useIntl } from 'react-intl';
import { FormattedMessage } from 'intl';
import type { ExportToDocumentationResultData } from 'shared/domain/issue/linkedDocumentation/operations';
import {
  exportedVersionSettingNotSavedKey,
  formatMapUnsuitableReason,
  versionAddedToastKey,
} from 'intl/helpers/documentation';
import { findMapUnsuitableReason } from 'shared/domain/documentation/mapUnsuitableReason';
import { formatMapLocation, isLevelMapDocumentation } from 'shared/domain/documentation/mapLocation';
import { formatVersionNumber } from 'shared/domain/documentation/versionState';
import { CommonTranslationKeys, DocumentationTranslationKeys } from 'intl/keys';

import { ButtonText } from 'components/general';
import { DocumentationBrowserDialog } from 'components/documentation/DocumentationBrowser';
import { AddVersionDialog } from 'components/documentation/AddVersionDialog';
import { useExportToDocumentation } from './ExportToDocumentationContext';
import { useExportToDocumentationBrowser } from './useExportToDocumentationBrowser';
import {
  getDefaultVersionName,
  getExportedFileName,
  getNextVersionNumber,
  toExportedVersionOptions,
  type ExportedVersionChoice,
} from './model';
import { useIssueRouteParams } from 'hooks/navigation/useRouteParams';
import { openDirectoryForm } from 'presentation/directory/openDirectoryInput';
import { isNonEmptyArray } from 'shared/utils/array';
import { isErrorWithMessage } from 'shared/utils/errorHandling';
import { useDocumentationStore } from 'store/documentation';
import { ROOT_DIR_ID } from 'shared/constants/directory';
import {
  showErrorToastAndLog,
  showToast,
  type ToasterMessage,
} from 'services/toaster';
import { TOASTER_TYPES } from 'components/core/Toaster/types';
import { useIssueDocumentationOperations, useIssueStore, type ExportAsVersion } from 'store/issue';
import { exportHintSx } from './styles';
import { useAutoEnterUploadedDirectory } from './useAutoEnterUploadedDirectory';
import { useCurrentIssue } from 'components/dataProviders/withIssue';
import {
  createLinkedDocumentationItem,
  isAlreadyLinked,
  LinkedDocumentationType,
} from 'shared/domain/issue/linkedDocumentation';

/** Tells which settings of an exported version could not be saved, while the export itself succeeded. */
function showUnsavedVersionSettings({ unsavedVersionSettings = [] }: ExportToDocumentationResultData): void {
  unsavedVersionSettings.forEach((setting) =>
    showToast({ message: { id: exportedVersionSettingNotSavedKey(setting) }, type: TOASTER_TYPES.FAILURE })
  );
}

export function ExportToDocumentationDialog(): ReactElement {
  const { filesToExport, isOpen, closeExportDialog } =
    useExportToDocumentation();
  const exportableFiles = useMemo(
    () => filesToExport.filter((f) => f._id && isFileExportAllowed(f)),
    [filesToExport]
  );
  const [exportedFile] = exportableFiles;
  const hasExportableFiles = isNonEmptyArray(exportableFiles);
  // A version is added from one file at a time: with several files a documentation cannot be picked.
  const canExportAsVersion = exportableFiles.length === 1;
  const browser = useExportToDocumentationBrowser(isOpen, canExportAsVersion);
  const targetDocumentation = canExportAsVersion
    ? browser.selectedDocumentation
    : undefined;
  const [isVersionDialogOpen, setIsVersionDialogOpen] = useState(false);
  const closeVersionDialog = useCallback(() => setIsVersionDialogOpen(false), []);
  const intl = useIntl();
  const exportedFileExtension = exportedFile?.data?.extension;
  // The sublocations the target documentation is the map of: the version dialog warns what becomes of the map.
  const targetMapLocation =
    targetDocumentation && isLevelMapDocumentation(targetDocumentation)
      ? formatMapLocation(targetDocumentation)
      : undefined;
  // Set before the store's pending flag reaches the dialogs: a second click starts no second export.
  const isExportingRef = useRef(false);
  const { issueId } = useIssueRouteParams();
  const resyncAllDirectories = useDocumentationStore.use.resyncAllDirectories();
  const fetchDirectoryContent = useDocumentationStore.use.fetchDirectoryContent();
  const resyncIssue = useIssueStore.use.resyncCurrentIssue();
  const { exportToDocumentation, exportPending } =
    useIssueDocumentationOperations();
  const resetLinkedDocumentationPreview =
    useIssueStore.use.resetLinkedDocumentationPreview();
  const resetLinkedDocumentationDetails =
    useIssueStore.use.resetLinkedDocumentationDetails();
  const issue = useCurrentIssue();
  const setLinkedDocumentation = useIssueStore.use.setLinkedDocumentation();

  useAutoEnterUploadedDirectory({ isOpen, browser });

  /** What the toast tells once a single file is exported: as a new documentation, or as which version of which one. */
  const getExportedToastMessage = useCallback(
    (result: ExportToDocumentationResultData, asVersion: ExportAsVersion | undefined): ToasterMessage | undefined => {
      if (!exportedFile) return undefined;
      if (!asVersion || !targetDocumentation) {
        return {
          id: DocumentationTranslationKeys.exportedAsNewToast,
          messageParams: { file: getExportedFileName(exportedFile) },
        };
      }
      const { makeCurrent } = asVersion.version;
      const unsuitableReason =
        makeCurrent && targetMapLocation ? findMapUnsuitableReason({ extension: exportedFileExtension }) : undefined;
      if (unsuitableReason && targetMapLocation) {
        return {
          id: DocumentationTranslationKeys.mapCurrentVersionNotSuitable,
          messageParams: {
            reason: formatMapUnsuitableReason(intl, unsuitableReason, exportedFileExtension),
            location: targetMapLocation,
          },
        };
      }
      return {
        id: versionAddedToastKey(Boolean(makeCurrent)),
        messageParams: {
          version: formatVersionNumber(getNextVersionNumber(targetDocumentation)) ?? '',
          documentation: result.documentationName || targetDocumentation.name,
        },
      };
    },
    // The documentation as it was before the export: the toast names the number the version was added with.
    [exportedFile, exportedFileExtension, intl, targetDocumentation, targetMapLocation]
  );

  const handleExport = useCallback(async (asVersion?: ExportAsVersion) => {
    if (!issueId || !hasExportableFiles || isExportingRef.current) return;
    isExportingRef.current = true;

    let linkedDocumentation = issue?.linkedDocumentation ?? [];
    let failedCount = 0;
    let lastResult: ExportToDocumentationResultData | undefined;

    for (const file of exportableFiles) {
      try {
        const result = await exportToDocumentation({
          issueId,
          fileToExport: file,
          currentDirectoryId: browser.currentDirectoryId,
          selectedDirectoryId: browser.selectedDirectoryId,
          ...asVersion,
        });

        const alreadyPresent = isAlreadyLinked(linkedDocumentation, {
          type: LinkedDocumentationType.version,
          documentationId: result.documentationId,
          versionId: result.versionId,
        });

        if (!alreadyPresent) {
          const optimisticItem = createLinkedDocumentationItem({
            type: LinkedDocumentationType.version,
            documentationId: result.documentationId,
            versionId: result.versionId,
            sourceDocumentId: typeof file._id === 'string' ? file._id : undefined,
            nameCandidates: [
              result.documentationName,
              file.description,
              file.title,
              file.data?.name,
            ],
            descriptionCandidates: [file.description],
            imageRotationCandidates: [file.imageRotation],
          });

          linkedDocumentation = [...linkedDocumentation, optimisticItem];
        }

        lastResult = result;
        showUnsavedVersionSettings(result);
        resetLinkedDocumentationPreview(String(result.versionId));
        resetLinkedDocumentationDetails(String(result.documentationId));
      } catch (err) {
        failedCount += 1;
        const message = isErrorWithMessage(err)
          ? String(err.message)
          : undefined;
        if (message && message !== 'Something went wrong') {
          showToast({ message, type: TOASTER_TYPES.FAILURE });
        } else {
          showErrorToastAndLog('Export to documentation failed', err);
        }
      }
    }

    try {
      if (failedCount < exportableFiles.length) {
        setLinkedDocumentation(issueId, linkedDocumentation);
        documentationClient.sync();
        resyncAllDirectories();
        const targetDirectoryId =
          browser.selectedDirectoryId ??
          browser.currentDirectoryId ??
          ROOT_DIR_ID;
        await fetchDirectoryContent(targetDirectoryId, {
          suppressLoadingIndicators: true,
        });
        resyncIssue({ force: true });
      }
      const exportedToastMessage =
        lastResult && canExportAsVersion ? getExportedToastMessage(lastResult, asVersion) : undefined;
      if (exportedToastMessage) {
        showToast({ message: exportedToastMessage, type: TOASTER_TYPES.SUCCESS });
      }
    } finally {
      isExportingRef.current = false;
      closeVersionDialog();
      closeExportDialog();
    }
  }, [
    browser.currentDirectoryId,
    browser.selectedDirectoryId,
    fetchDirectoryContent,
    closeExportDialog,
    closeVersionDialog,
    exportableFiles,
    exportToDocumentation,
    hasExportableFiles,
    issue,
    issue?.linkedDocumentation,
    issueId,
    resyncAllDirectories,
    resyncIssue,
    setLinkedDocumentation,
    resetLinkedDocumentationDetails,
    resetLinkedDocumentationPreview,
    canExportAsVersion,
    getExportedToastMessage,
  ]);

  const handleConfirm = useCallback(() => {
    if (targetDocumentation) {
      setIsVersionDialogOpen(true);
      return;
    }
    handleExport();
  }, [handleExport, targetDocumentation]);

  const handleConfirmVersion = useCallback(
    (choice: ExportedVersionChoice) => {
      if (!targetDocumentation?._id || !exportedFile) return;

      handleExport({
        targetDocumentationId: targetDocumentation._id,
        version: toExportedVersionOptions(choice, getDefaultVersionName(exportedFile)),
      });
    },
    [exportedFile, handleExport, targetDocumentation]
  );

  const handleNewDirectory = useCallback(() => {
    openDirectoryForm(undefined, {
      parentId: browser.currentDirectoryId || undefined,
    });
  }, [browser.currentDirectoryId]);

  if (!hasExportableFiles) return <></>;

  return (
    <>
      <DocumentationBrowserDialog
        isOpen={isOpen}
        onClose={closeExportDialog}
        titleFallbackId={CommonTranslationKeys.exportToDocumentation}
        searchDataQa='export-to-documentation-search'
        browser={browser}
        options={
          canExportAsVersion && exportedFile ? (
            <Typography
              variant='body2'
              color='text.secondary'
              sx={exportHintSx}
              data-qa='export-to-documentation-hint'
            >
              {getExportedFileName(exportedFile)}
              {' · '}
              {targetDocumentation ? (
                <FormattedMessage id={DocumentationTranslationKeys.exportHintAsVersion} />
              ) : (
                <FormattedMessage id={DocumentationTranslationKeys.exportHintAsNew} />
              )}
            </Typography>
          ) : undefined
        }
        leftActions={
          <ButtonText onClick={handleNewDirectory}>
            <FormattedMessage id={DocumentationTranslationKeys.createDirectory} />
          </ButtonText>
        }
        confirmLabelId={CommonTranslationKeys.export}
        onConfirm={handleConfirm}
        confirmDisabled={
          exportPending ||
          browser.loading ||
          !issueId ||
          !hasExportableFiles ||
          Boolean(browser.loadError)
        }
        confirmLoading={exportPending || browser.loading}
      />
      {targetDocumentation && exportedFile && (
        <AddVersionDialog
          isOpen={isVersionDialogOpen}
          documentationName={targetDocumentation.name}
          fileName={getExportedFileName(exportedFile)}
          fileExtension={exportedFileExtension}
          mapLocation={targetMapLocation}
          versionNumber={getNextVersionNumber(targetDocumentation)}
          defaultName={getDefaultVersionName(exportedFile)}
          loading={exportPending}
          onConfirm={handleConfirmVersion}
          onCancel={closeVersionDialog}
        />
      )}
    </>
  );
}
